<template>
  <div>
    <div class="text-center">
      <v-overlay :value="isLoading">
        <v-progress-circular
          indeterminate
          size="64"
        />
      </v-overlay>
    </div>

    <v-alert
      v-if="showNoDataError"
      outlined
      dense
      type="error"
    >
      <div v-if="isShareAssetForm()">
        Please ensure that you have saved the share data
      </div>
      <div v-if="isAssetForm()">
        Please ensure that you have added and saved at least one asset
      </div>
    </v-alert>
    <form-receiving-asset v-model="whoAssets" />

    <v-card
      v-if="isShareAssetForm() && !isLoading"
      flat
    >
      <form-share
        :loading="isLoading"
        :share-values="formValues"
        @onSubmitData="onShareActionData"
        @onCancelClick="onCancelAction"
      />
    </v-card>

    <template v-if="isAssetForm()">
      <v-card flat>
        <v-card-title>{{ assetTitle }}</v-card-title>
        <v-data-table
          :headers="assetHeaders"
          :items="assetItems"
          :items-per-page="5"
        >
          <template #[`item.assetType`]="{ item }">
            <asset-type-label :asset-type="item.assetType" />
          </template>
          <template #[`item.actions`]="{ item }">
            <table-actions
              :actions="getActionsTable(item)"
              @action="onActionEvent"
            />
          </template>
        </v-data-table>
      </v-card>

      <v-card flat>
        <div v-if="isAddItem">
          <v-select
            v-model="assetType"
            label="Asset Type"
            :items="assetTypes"
            item-text="label"
            item-value="id"
            :rules="rules.assetType"
            required
          />

          <form-asset
            :loading="isLoading"
            :asset-values="formValues"
            :asset-type="assetType"
            @onSubmitData="onAssetActionData"
            @onCancelClick="onCancelAction"
          />
        </div>
        <v-card-actions v-if="!isAddItem">
          <v-btn
            color="primary"
            large
            @click.stop="onAddItem()"
          >
            Add Assets
          </v-btn>
        </v-card-actions>
      </v-card>
    </template>
  </div>
</template>

<script>
import { firebase } from '@/firebase'
import AssetTypeLabel from '@/components/AssetTypeLabel.vue'
import FormAsset from './FormAsset.vue'
import FormShare from './FormShare.vue'
import FormReceivingAsset from './FormReceivingAsset.vue'
import TableActions from '@/components/TableActions.vue'

import {
  updateContentAssetData,
  getContentAssetData
} from '@/dataAccess/documents'
import {
  getDocumentType,
  isSharePurchase
} from '@/dataAccess/documentTypes'
import { createAssetId } from '@/utils/assetId'
import {
  ASSET_TYPES_LIST,
  DEFAULT_ASSET_TYPE,
  getAssetType
} from '@/dataAccess/assetTypes'
import { DEFAULT_CURRENCY_TYPE } from '@/dataAccess/currencies'

const DEFAULT_HEADERS = {
  text: null,
  value: null,
  align: 'center',
  sortable: true,
  filterable: false,
  groupable: false,
  divider: false,
  class: 'data-table',
  cellClass: '',
  width: ''
}
const ASSET_HEADERS = [
  { ...DEFAULT_HEADERS, text: 'Asset Type', value: 'assetType' },
  { ...DEFAULT_HEADERS, text: 'Asset Id', value: 'assetId' },
  { ...DEFAULT_HEADERS, text: 'Asset Name', value: 'assetName' },
  { ...DEFAULT_HEADERS, text: 'Asset Value', value: 'assetValue' },
  { ...DEFAULT_HEADERS, text: 'Currency', value: 'currencyType' },
  { ...DEFAULT_HEADERS, text: 'Actions', value: 'actions', sortable: false }
]

const SHARES = 'shares'
const ASSETS = 'assets'

const ACTION_EVENTS = {
  editItem: 'editItem',
  removeItem: 'removeItem'
}

export default {
  name: 'FormAssetInput',
  components: {
    AssetTypeLabel,
    FormAsset,
    FormShare,
    TableActions,
    FormReceivingAsset
  },
  props: {
    documentRef: { type: Object, default: () => {} },
    showNoDataError: { type: Boolean, default: false },
    assetTitle: { type: String, default: 'List Asset Data' },
    shareTitle: { type: String, default: 'List Share Data' }
  },
  data() {
    return {
      assetHeaders: ASSET_HEADERS,
      isLoading: true,
      isAddItem: false,
      assetItems: [],
      shareItems: [],
      formValues: null,
      assetType: null,
      assetTypes: ASSET_TYPES_LIST,
      whoAssets: null,
      document: null,
      rules: {
        assetType: [
          (value) => {
            return (
              !!getAssetType(value, { allowMissing: true }) ||
              'Select a valid asset type property'
            )
          }
        ]
      }
    }
  },
  watch: {
    async whoAssets(newVal, oldVal) {
      if (newVal !== oldVal) {
        await this.documentRef.update({
          whoIsReceivingAssets: newVal
        })
      }
    }
  },
  beforeMount() {
    this.isLoading = true
    this.getDocument()
    this.getAssetData()
  },
  methods: {
    isEqual(obj, item) {
      return obj.uuid === item.uuid
    },
    editItem(item, type) {
      if (type !== ASSETS) {
        return
      }

      this.assetType = item.assetType
      this.assetItems = this.assetItems.filter(
        (obj) => !this.isEqual(obj, item)
      )
      this.formValues = item
      this.isAddItem = true
    },
    removeItem(item, type) {
      if (type !== ASSETS) {
        return
      }

      const assetShareData = this.assetItems.filter(
        (obj) => !this.isEqual(obj, item)
      )
      this.assetType = null
      this.onSubmit(assetShareData, type)
    },
    getActionsTable(item) {
      const deleteAsset = {
        hoverColor: 'primary',
        className: '',
        title: 'Remove asset',
        event: 'removeItem',
        icon: 'delete',
        type: 'assets',
        item
      }
      return [
        {
          hoverColor: 'primary',
          className: 'mr-2',
          title: 'Edit asset',
          event: 'editItem',
          icon: 'pencil',
          type: 'assets',
          item
        },
        this.assetItems.length === 1 ? { ...deleteAsset, isDisable: true, disabledReason: 'Contract must have at least one asset' } : deleteAsset
      ]
    },
    onAddItem() {
      this.assetType = DEFAULT_ASSET_TYPE.id
      this.isAddItem = true
    },
    onAssetActionData(formData) {
      this.assetItems = [...this.assetItems, formData]
      this.formValues = null
      this.isAddItem = false
      this.onSubmit(this.assetItems, ASSETS)
    },
    onAssetSingleActionData(formData) {
      if (!formData) {
        this.assetItems = []
        this.formValues = null
      } else {
        this.assetItems = [formData]
      }

      this.onSubmit(this.assetItems, ASSETS)
    },
    onShareActionData(formData) {
      // At the moment is required to add just one share register
      if (!formData) {
        this.shareItems = []
        this.formValues = null
      } else {
        this.shareItems = [
          { ...formData, assetType: this.document.documentType }
        ]
      }

      this.onSubmit(this.shareItems, SHARES)
    },
    onActionEvent({ actionEvent, itemValue, type }) {
      try {
        const handler = ACTION_EVENTS[actionEvent]
        this[handler](itemValue, type)
      } catch (error) {
        this.$showErrorbar(
          'Unable to call the event, please check that the action event match with your request.'
        )
      }
    },
    onCancelAction(type) {
      this.isLoading = true
      this.isAddItem = false
      this.assetType = null
      this.formValues = null
      this.getAssetData()
    },
    onSubmit(data, type) {
      this.isLoading = true
      const assetShareData = {
        [type]: data
      }
      this.pushAssetData(assetShareData)
    },
    isAssetForm() {
      return !this.isShareAssetForm()
    },
    isOneOf(...docTypeFuncs) {
      return (
        this.document &&
        docTypeFuncs.some((func) => func(this.document.documentType))
      )
    },
    isShareAssetForm() {
      return this.isOneOf(isSharePurchase)
    },
    addDefaultData() {
      if (this.isShareAssetForm()) {
        this.formValues = this.shareItems[0]
        this.assetType = this.document.documentType
        return
      }

      if (this.assetItems.length === 0) {
        const assetType = getAssetType(this.document.documentType, {
          allowMissing: true
        })
        if (assetType) {
          this.assetType = assetType.id

          this.assetItems.push({
            assetType: this.assetType,
            assetId: createAssetId(),
            assetName: getDocumentType(this.document.documentType).label,
            assetValue: '0',
            details: null,
            currencyType: DEFAULT_CURRENCY_TYPE.id
          })
          this.onSubmit(this.assetItems, ASSETS)
        }
      }
    },
    async pushAssetData(assetData) {
      const metadata = {
        modified: firebase.firestore.FieldValue.serverTimestamp(),
        modifiedBy: this.$store.state.user.uid
      }

      try {
        await updateContentAssetData(this.documentRef, assetData, metadata)
        this.getAssetData()
      } catch (error) {
        this.$showErrorbar(
          'Unable to save. Please check your internet connection.'
        )
        this.$Sentry.captureException(error)
      }
    },
    async getDocument() {
      const data = await this.documentRef.get()
      if (data.exists) {
        this.document = data.data()
        this.whoAssets = this.document.whoIsReceivingAssets || null
      } else {
        this.$showErrorbar('Impossible to load document data')
      }
    },
    async getAssetData() {
      await getContentAssetData(this.documentRef)
        .get()
        .then((doc) => {
          if (doc.exists) {
            const assetData = doc.data()
            this.assetItems = assetData[ASSETS] || []
            this.shareItems = assetData[SHARES] || []
          } else {
            this.assetItems = []
            this.shareItems = []
          }
          this.addDefaultData()
          this.isLoading = false
        })
        .catch((error) => {
          this.isLoading = false
          this.$showErrorbar(
            'Unable to retrieve assetData. Please check your internet connection.'
          )
          this.$Sentry.captureException(error)
        })
    }
  }
}
</script>
